import {
  Grid,
  IconButton,
  InputAdornment,
  Modal,
  OutlinedInput,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Add, Delete } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  AddButton,
  AssociationModalContainer,
  Label,
  LearnerVisibilityToggle,
  RootContainer,
  StakeholdersListContainer,
} from "@components/StakeholdersCst/style";
import Switch from "@mui/material/Switch";
import StakeholdersCstProps from "./types";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import InputCst from "@components/InputCst/InputCst";
import { scrollToElement } from "@utils/utilsScrollToElement";
import AssociationTableCst from "@components/AssociationTableCst";
import StakeholdersCstRow from "@components/StakeholdersCst/components/StakeholdersCstRow";
import ButtonCst from "@components/ButtonCst";

export interface Stakeholder {
  userId?: number;
  name?: string;
  type: string;
  visStakeholder: boolean;
}

const StakeholdersCst = ({
  label,
  disabled,
  stakeholders,
  corporate_id,
  onChange,
}: StakeholdersCstProps) => {
  const theme = useTheme();

  const { t } = useTranslation(["stakeholders_section", "form"]);
  const [selectedStakeholders, setSelectedStakeholders] = useState<
    Stakeholder[]
  >(stakeholders || []);
  const [openUserAssociation, setOpenUserAssociation] = useState(false);
  const [userAssociation, setUserAssociation] = useState<any>([]);
  const [error, setError] = useState<string[]>([]);
  const [formData, setFormData] = useState<Stakeholder>({
    userId: undefined,
    name: undefined,
    type: "",
    visStakeholder: false,
  });

  useEffect(() => {
    setSelectedStakeholders(stakeholders ?? []);
  }, [stakeholders]);

  const addStakeholders = () => {
    if (areFieldsValidated()) {
      const updatedList = [...selectedStakeholders, formData];

      setSelectedStakeholders(updatedList);
      onChange(updatedList);

      setFormData({
        userId: undefined,
        name: undefined,
        type: "",
        visStakeholder: false,
      });
      setUserAssociation([]);
    }
  };

  const delStakeholder = (selectedIndex: number) => {
    const updatedList = selectedStakeholders.filter(
      (_, index) => index !== selectedIndex,
    );
    setSelectedStakeholders(updatedList);
    onChange(updatedList);
  };

  const onUserAssociation = (selected: any) => {
    const mappedSelectedUser: any = Object.keys(selected).map((key: string) => {
      if (key.includes("||")) {
        return {
          id: key.split("||")[0],
          label: key.split("||")[1],
        };
      }
    });

    if (!mappedSelectedUser || mappedSelectedUser.length === 0)
      return setOpenUserAssociation(false);

    const isAlreadySelected =
      stakeholders &&
      stakeholders
        .map((stakeholder) => stakeholder.userId)
        .includes(mappedSelectedUser[0].id);

    if (
      mappedSelectedUser &&
      mappedSelectedUser.length > 0 &&
      !isAlreadySelected
    ) {
      setFormData((current) => ({
        ...current,
        userId: Number(mappedSelectedUser[0].id),
        name: mappedSelectedUser[0].label,
      }));
    }

    setUserAssociation(mappedSelectedUser);
    setOpenUserAssociation(false);
  };

  const areFieldsValidated = () => {
    const errorFields = [];
    if (!formData.userId || !formData.name) errorFields.push("user");
    if (!formData.type) errorFields.push("role");
    setError(errorFields);
    return !(errorFields.length > 0);
  };

  return (
    <>
      <Grid item xs={12}>
        <RootContainer theme={theme}>
          <Label theme={theme} disabled={disabled}>
            {label}
          </Label>

          <Grid container spacing={"24px"}>
            <Grid item xs={12}>
              <OutlinedInput
                required
                error={error.includes("user") && !formData.userId}
                onClick={() => setOpenUserAssociation(true)}
                size={"small"}
                readOnly
                disabled={disabled}
                value={formData.name ?? t("select_stakeholder")}
                sx={{
                  width: "100%",
                  cursor: "pointer",
                }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setOpenUserAssociation(true)}
                      edge="end"
                    >
                      <Add />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <InputCst
                error={error.includes("role") && !formData.type}
                label={t("select_role") ?? ""}
                placeholder={t("select_role") ?? ""}
                value={formData.type}
                disabled={disabled}
                onChange={(e) =>
                  setFormData((current) => ({
                    ...current,
                    type: e.target.value,
                  }))
                }
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <LearnerVisibilityToggle gap={2}>
                <Typography>{t("show_to_learner")}</Typography>
                <Switch
                  disabled={disabled}
                  checked={formData.visStakeholder}
                  onChange={(_, checked) =>
                    setFormData((current) => ({
                      ...current,
                      visStakeholder: checked,
                    }))
                  }
                />
              </LearnerVisibilityToggle>
            </Grid>

            {!disabled && (
              <Grid item>
                <ButtonCst
                  disabled={disabled}
                  id={"addStakeholder"}
                  variant={"text"}
                  startIcon={<Add />}
                  onClick={addStakeholders}
                >
                  {t("form:add")}
                </ButtonCst>
              </Grid>
            )}

            {selectedStakeholders && selectedStakeholders.length > 0 && (
              <Grid item xs={12}>
                <StakeholdersListContainer gap={2}>
                  {selectedStakeholders.map((stakeholder, index) => {
                    return (
                      <StakeholdersCstRow
                        key={`stkhldr_${stakeholder.userId}`}
                        stakeholder={stakeholder}
                        onDelete={() => delStakeholder(index)}
                        disabled={disabled}
                      />
                    );
                  })}
                </StakeholdersListContainer>
              </Grid>
            )}
          </Grid>
        </RootContainer>
      </Grid>
      <Modal
        open={openUserAssociation}
        onClose={() => setOpenUserAssociation(false)}
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        }}
      >
        <AssociationModalContainer theme={theme}>
          <AssociationTableCst
            enableFullScreenToggle={false}
            association={userAssociation}
            callBackAssociation={onUserAssociation}
            setAssociation={() => {}}
            setCloseAssociation={(value: boolean) => {
              scrollToElement("user_id");
              setOpenUserAssociation(false);
            }}
            service={`${ServicesURL.getCustomer}?corporate_id=${corporate_id}`}
            pageID={PagesID["stakeholders.corporate.customers"]}
            title={t("association_modal_title")}
            enableMultiRowSelection={false}
          />
        </AssociationModalContainer>
      </Modal>
    </>
  );
};

export default StakeholdersCst;
